import {WinPresentationController} from "../../WinPresentationController";
import {TheRave} from "../../../TheRave";
import {gsap, TimelineLite} from "gsap";
import {IBetWayWinData, IBetWinData} from "../../../server/data/ITheRaveData";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {TheRaveGameConfig} from "../../../TheRaveGameConfig";
import {TheRaveSound} from "../../../sound/TheRaveSound";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";

export class BetWaysInitialWinController extends WinPresentationController {

    private _mainTimeline: TimelineLite | null;
    private _belowStakeWinRestriction:boolean;
    private _skipped:boolean;

    constructor() {
        super("BetWaysInitialWinController", true);
    }

    protected hasWin(): boolean {
        this._skipped = false;
        //TheRave.multiplier.verifyTotalMultiplier();
        return TheRave.model.parsedGameData.betWins.length > 0 && TheRave.model.parsedGameData.singleWin > 0;
    }

    private getBelowStakeWinRestriction():boolean {
        if(!TheRave.apiPlugin.gameClientConfiguration.belowStakeWinRestriction) {
            return false;
        }

        const data = TheRave.model.parsedGameData;
        let playedBetValue = data.playedBetValue;
        if(this.getEnhancedBet()) {
            playedBetValue *= 1.1;
        }


        return (data.singleWin / playedBetValue) <= 1;
    }

    private getBelowStakeWinRestrictionAccWin():boolean {
        if(!TheRave.apiPlugin.gameClientConfiguration.belowStakeWinRestriction) {
            return false;
        }

        const data = TheRave.model.parsedGameData;
        let playedBetValue = data.playedBetValue;
        if(this.getEnhancedBet()) {
            playedBetValue *= 1.2;
        }

        // Take feature buy win below stake into account
        if((data.singleWin / playedBetValue) <= 1 && !TheRave.isWinBelowStake()) {
            return (data.totalWin / playedBetValue) <= 1;
        }
        else {
            return (data.singleWin / playedBetValue) <= 1;
        }
    }

    private getEnhancedBet():boolean {
        const data = TheRave.model.parsedGameData;
        if(data.enhancedBet) {
            return true;
        }

        // below fake for UKGC when restore in fs.
        if(!data.mode.includes("FREESPIN")) {
            return false;
        }

        if(TheRave.model.isRestore) {
            return true;
        }

        //if(TheRave.reels[1] && TheRave.reels[1].isEnhancedBeforeFS()) { //TODO: What does this do?? Check if enhanced bet is active?
            //return true;
        //}

        return false;
    }

    protected async createWinAnimation(): Promise<any> {
        if (TheRave.isWinBelowStake()) {
            const belowStakeRestriction = (this.getBelowStakeWinRestrictionAccWin() && !TheRave.model.data.wasFeatureBuy) || TheRave.isWinBelowStake();
            this._belowStakeWinRestriction = this.getBelowStakeWinRestriction();

            const data = TheRave.model.parsedGameData;
            (<IBetWayWinData[]>data.betWins).forEach((w: IBetWayWinData) => {
                w.reelWinPositions.forEach((pos: number[]) => {
                    const sym = TheRave.reels[pos[0]].getSymbol(pos[1], false, false);
                    if (!sym.isFullWild) {
                        TheRave.reels[pos[0]].reelView.addWinSymbol(sym);
                    }
                });
            });
            SlotGame.keypadPlugin.setWin(data.totalWin, data.showTotalWin, belowStakeRestriction);
            await Promise.resolve();
            this._mainTimeline = null;
            return;
        }
        const tl = new TimelineLite();
        this.getWinAnimation(tl);
        this.getWinCountUpAnimation(tl);
        this._mainTimeline = tl;
        tl.to(this, {duration: 0.1});
        await Promise.resolve(this._mainTimeline);
        // TODO: Tl sanity
        //this._mainTimeline = null;
    }

    protected getWinAnimation(tl: TimelineLite):void {
        const winRatio = TheRave.model.parsedGameData.currentWinRatioConfig?.ratio!;
        let winDelay = 0;
        let winWaysWon = 0;
        TheRave.model.parsedGameData.betWins.forEach(w => {
            winWaysWon += (<IBetWayWinData>w).numberOfCombinations;
            tl.add(this.getSingleBetWinAnimation(<IBetWayWinData>w, winRatio, winWaysWon), 0);
        });
        tl.add(()=>{
            TheRaveSound.playNormalWinBetLineSound(TheRave.model.parsedGameData.currentWinRatioConfig!);
        }, 0.2);
        tl.add(TheRave.waysWon.showWinWaysWon(winWaysWon, TheRave.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1), 0.2);

    }

    protected getSingleBetWinAnimation(winData: IBetWayWinData, winRatio: number, winWaysWon: number): TimelineLite {
        const tl = new TimelineLite(),
            winNumPlayPosition: number[] = TheRave.individualWin.view.findLastNoWildSymbolPosition(winData);
        winData.reelWinPositions.forEach((pos: number[]) => {
            tl.add(this.getSymbolWinAnimation(pos[0], pos[1], winRatio), 0);
        });
        if(TheRave.apiPlugin.gameClientConfiguration.displayIndividualWinsInGame){
            tl.add(TheRave.individualWin.view.playWinNumbers(winData, false, winNumPlayPosition),0);
        }
        // add WinWaysWon animation.
        return tl;
    }

    protected getSymbolWinAnimation(reelId: number, symId: number, winRatio: number): TimelineLite {
        const tl = new TimelineLite();

        const sym = TheRave.reels[reelId].getSymbol(symId, false, false);
        tl.add((sym.isFullWild) ? () => {
            if(symId === 0) {
                TheRave.reels[reelId].reelView.addWinSymbol(sym);
            }
        } : () => TheRave.reels[reelId].reelView.addWinSymbol(sym));// no need to have win state in this game?
        tl.add(sym.initialWinFrameAnimation(winRatio));

        return tl;
    }

    private getReelDelay(winRatio: number): number {
        // tiny/small
        if (winRatio < TheRaveGameConfig.WIN_RATIOS[2].ratio) {
            return 0;
        }

        //mid
        if (winRatio < TheRaveGameConfig.WIN_RATIOS[3].ratio) {
            return 0.05;
        }

        // large and up
        return 0.1;
    }

    private getEffectLevel(winRatio: number): number {
        for (let i = 1; i < 4; i++) {
            if (winRatio < TheRaveGameConfig.WIN_RATIOS[i].ratio) {
                return i - 1;
            }
        }
        return 3;
    }

    protected getWinCountUpAnimation(tl:TimelineLite): void {
        const data = TheRave.model.parsedGameData;
        if (data.currentWinRatioConfig!.bigWinLevel > -1) {
            Logger.logDev("No count up for big win from BetWaysInitialWinController.")
            return; // no count up for big win.
        }

        tl.add(TheRave.initialCountUp.getCountUpAnimation(0, data.singleWin, data.currentWinRatioConfig!.duration, data.currentMultiplier), 0.4);
        tl.add(() => {
            SlotGame.keypadPlugin.setWin(data.totalWin, data.showTotalWin, TheRave.isWinBelowStake());
            TheRave.initialCountUp.remove();
            TheRave.waysWon.remove();
        });
    }

    protected skip():void {
        const data = TheRave.model.parsedGameData;
        if((TheRave.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1)){
            return;
        }


        let isPlayed:boolean = false;
       this._skipped = true;
        if(this._mainTimeline){
            if(this._mainTimeline.isActive()){
                isPlayed = true;
                this._mainTimeline?.progress(1);
            }
        }
        if (data.currentWinRatioConfig!.bigWinLevel > -1 || TheRave.isWinBelowStake()) {
            return; // no count up for big win.
        }
        TheRaveSound.stopSoundEffect("countup");
        if(!isPlayed){
            TheRave.initialCountUp.getCountUpAnimation(data.singleWin, data.singleWin, 0, data.currentMultiplier);
            TheRaveSound.playNormalWinSound(TheRave.model.parsedGameData.currentWinRatioConfig!);
        }
    }
}